import { create } from "zustand";

export const CREATION_RECOVERY_PREFIX = "vend:creationRecovery:";
const creationRecoveryLifetime = 24 * 60 * 60 * 1000;
const creationRecoveryLimit = 512 * 1024;
export type CreationRecovery<T> = { value: T; operationId: string | null };

export function readCreationRecovery<T>(scope: string, entity: "machine" | "location" | "expense"): CreationRecovery<T> | null {
  try {
    const raw = sessionStorage.getItem(`${CREATION_RECOVERY_PREFIX}${scope}:${entity}`);
    if (!raw) return null;
    const saved = raw.length <= creationRecoveryLimit ? JSON.parse(raw) : null;
    if (saved?.version !== 1 || saved.scope !== scope || saved.entity !== entity ||
        typeof saved.savedAt !== "number" || !Number.isFinite(saved.savedAt) ||
        (saved.operationId === null && (Date.now() - saved.savedAt < 0 || Date.now() - saved.savedAt >= creationRecoveryLifetime)) ||
        !saved.value || typeof saved.value !== "object" || Array.isArray(saved.value) ||
        (saved.operationId !== null && (typeof saved.operationId !== "string" || !/^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(saved.operationId)))) {
      sessionStorage.removeItem(`${CREATION_RECOVERY_PREFIX}${scope}:${entity}`);
      return null;
    }
    return { value: saved.value, operationId: saved.operationId };
  } catch {
    try { sessionStorage.removeItem(`${CREATION_RECOVERY_PREFIX}${scope}:${entity}`); } catch { /* Storage may be unavailable. */ }
    return null;
  }
}

export function writeCreationRecovery<T>(scope: string, entity: "machine" | "location" | "expense", recovery: CreationRecovery<T> | null): boolean {
  try {
    const key = `${CREATION_RECOVERY_PREFIX}${scope}:${entity}`;
    if (recovery === null) { sessionStorage.removeItem(key); return true; }
    const serialized = JSON.stringify({ version: 1, scope, entity, savedAt: Date.now(), ...recovery });
    if (serialized.length > creationRecoveryLimit) { sessionStorage.removeItem(key); return false; }
    sessionStorage.setItem(key, serialized);
    return true;
  } catch {
    try { sessionStorage.removeItem(`${CREATION_RECOVERY_PREFIX}${scope}:${entity}`); } catch { /* Storage may be unavailable. */ }
    return false;
  }
}

export type DraftEntity =
  | "travel"
  | "task"
  | "machine"
  | "location"
  | "schedule"
  | "inventory"
  | "product"
  | "expense"
  | "expenseProject"
  | "expenseCategory"
  | "expensePaymentAccount"
  | "expenseMerchant"
  | "discoveryProgram"
  | "discoverySearch"
  | "qualificationRule"
  | "discoveryProvider"
  | "discoveryDemand"
  | "inventoryPolicy"
  | "marketplaceOpportunity"
  | "mailPreferences"
  | "mailMessage"
  | "mailConversation"
  | "appointment"
  | "appointmentType"
  | "salesLead"
  | "growthLead"
  | "profile"
  | "account"
  | "config";

export type CollectionFinancialDraft = {
  isEditing: boolean;
  grossCents: string;
  grossCashCents: string;
  grossCardCents: string;
  refundsCents: string;
  commissionCents: string;
  commissionPaymentType: "cash" | "check" | "direct_deposit" | "";
};

export type RefundFinancialDraft = {
  isEditing: boolean;
  refundsCents: string;
};

export type TaskDraftSections = {
  notes: { value: string };
  collectionFinancials: CollectionFinancialDraft;
  refundFinancials: RefundFinancialDraft;
};

export type EntityDraftMeta = {
  title: string;
  description?: string;
  path?: string;
  scrollTarget?: string;
  showInIndicator?: boolean;
  saveLabel?: string;
};

export const CONTINUE_DRAFT_EVENT = "vendistri:continue-draft";
export const SAVE_DRAFT_EVENT = "vendistri:save-draft";
export const DISCARD_DRAFT_EVENT = "vendistri:discard-draft";

type EditAwareDraftSection = { dirty?: boolean; editMode?: boolean };

export const entityDraftHasUnsavedChanges = (draft: { sections: Record<string, unknown> }) =>
  Object.values(draft.sections).some((section) => {
    if (!section || typeof section !== "object") return true;
    const edit = section as EditAwareDraftSection;
    return edit.editMode === true || edit.dirty !== false;
  });

type EntityDraft = {
  sections: Record<string, unknown>;
  externallyChanged?: boolean;
  meta?: EntityDraftMeta;
};

type EntityDraftState = {
  drafts: Record<string, EntityDraft>;
  patchDraft: (key: string, patch: Partial<EntityDraft>) => void;
  patchSection: <T extends object>(
    key: string,
    section: string,
    patch: Partial<T>,
    fallback: T,
    meta?: EntityDraftMeta,
  ) => void;
  clearSections: (key: string, sections: string[]) => void;
  clearDraft: (key: string) => void;
  markExternallyChanged: (keys: string[]) => void;
  reset: () => void;
};

export const entityDraftKey = (entity: DraftEntity, id: string) =>
  `${entity}:${id}`;

const pruneDraft = (draft: EntityDraft): EntityDraft | null => {
  if (!draft.externallyChanged && Object.keys(draft.sections).length === 0) {
    return null;
  }
  return draft;
};

const updateDraft = (
  drafts: Record<string, EntityDraft>,
  key: string,
  updater: (draft: EntityDraft) => EntityDraft,
) => {
  const nextDraft = pruneDraft(updater(drafts[key] || { sections: {} }));
  const nextDrafts = { ...drafts };
  if (nextDraft) {
    nextDrafts[key] = nextDraft;
  } else {
    delete nextDrafts[key];
  }
  return nextDrafts;
};

export const useEntityDraftStore = create<EntityDraftState>((set) => ({
  drafts: {},
  patchDraft: (key, patch) =>
    set((state) => ({
      drafts: updateDraft(state.drafts, key, (draft) => ({
        ...draft,
        ...patch,
        sections: patch.sections || draft.sections,
      })),
    })),
  patchSection: (key, section, patch, fallback, meta) =>
    set((state) => ({
      drafts: updateDraft(state.drafts, key, (draft) => ({
        ...draft,
        meta: meta || draft.meta,
        sections: {
          ...draft.sections,
          [section]: {
            ...((draft.sections[section] as object | undefined) || fallback),
            ...patch,
          },
        },
      })),
    })),
  clearSections: (key, sections) =>
    set((state) => ({
      drafts: updateDraft(state.drafts, key, (draft) => {
        const nextSections = { ...draft.sections };
        sections.forEach((section) => {
          delete nextSections[section];
        });
        return { ...draft, sections: nextSections };
      }),
    })),
  clearDraft: (key) =>
    set((state) => {
      const drafts = { ...state.drafts };
      delete drafts[key];
      return { drafts };
    }),
  markExternallyChanged: (keys) =>
    set((state) => {
      let drafts = state.drafts;
      keys.filter(Boolean).forEach((key) => {
        if (!drafts[key]) return;
        drafts = updateDraft(drafts, key, (draft) => ({
          ...draft,
          externallyChanged: true,
        }));
      });
      return drafts === state.drafts ? state : { drafts };
    }),
  reset: () => set({ drafts: {} }),
}));

export function useEntityDraftSection<
  Sections extends Record<string, object>,
  K extends keyof Sections & string,
>({
  entity,
  id,
  section,
  fallback,
  meta,
}: {
  entity: DraftEntity;
  id: string;
  section: K;
  fallback: Sections[K];
  meta?: EntityDraftMeta;
}) {
  const key = entityDraftKey(entity, id);
  const draft = useEntityDraftStore(
    (state) => state.drafts[key]?.sections[section] as Sections[K] | undefined,
  );
  const patchSection = useEntityDraftStore((state) => state.patchSection);
  const clearSections = useEntityDraftStore((state) => state.clearSections);

  const value = draft || fallback;
  return {
    draft,
    value,
    set: (next: Sections[K]) => {
      patchSection(key, section, next, fallback, meta);
    },
    patch: (patch: Partial<Sections[K]>) => {
      patchSection(key, section, patch, value, meta);
    },
    clear: () => {
      clearSections(key, [section]);
    },
  };
}
